import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ANOMALY_TYPES = {
  duplicate_charge: {
    label: 'Double Facturation',
    icon: 'Copy',
    color: 'bg-red-100 text-red-700 border-red-200',
    dotColor: 'bg-red-500',
    severity: 'critical',
    severityLabel: 'CRITIQUE',
    severityColor: 'bg-red-100 text-red-700',
  },
  amount_mismatch: {
    label: 'Divergence Montant',
    icon: 'DollarSign',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    dotColor: 'bg-orange-500',
    severity: 'high',
    severityLabel: 'ÉLEVÉ',
    severityColor: 'bg-orange-100 text-orange-700',
  },
  timing_anomaly: {
    label: 'Anomalie Temporelle',
    icon: 'Clock',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    dotColor: 'bg-amber-500',
    severity: 'medium',
    severityLabel: 'MOYEN',
    severityColor: 'bg-amber-100 text-amber-700',
  },
  rapid_succession: {
    label: 'Transactions Rapides',
    icon: 'Zap',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    dotColor: 'bg-yellow-500',
    severity: 'medium',
    severityLabel: 'MOYEN',
    severityColor: 'bg-yellow-100 text-yellow-700',
  },
  status_inconsistency: {
    label: 'Incohérence Statut',
    icon: 'AlertTriangle',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    dotColor: 'bg-purple-500',
    severity: 'low',
    severityLabel: 'FAIBLE',
    severityColor: 'bg-purple-100 text-purple-700',
  },
};

// ─── Anomaly Row ──────────────────────────────────────────────────────────────
const AnomalyRow = ({ anomaly, onDismiss, onEscalate, isNew }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = ANOMALY_TYPES?.[anomaly?.anomaly_type] || ANOMALY_TYPES?.amount_mismatch;

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${isNew ? 'ring-2 ring-red-400 ring-offset-1' : 'border-gray-200'}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Anomaly type badge */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg?.color?.split(' ')?.[0]}`}>
          <Icon name={cfg?.icon} size={16} className={cfg?.color?.split(' ')?.[1]} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg?.color}`}>
              <Icon name={cfg?.icon} size={9} />
              {cfg?.label}
            </span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${cfg?.severityColor}`}>
              {cfg?.severityLabel}
            </span>
            {isNew && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse">
                NOUVEAU
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-xs text-gray-700 font-mono truncate">{anomaly?.reference || anomaly?.id?.slice(0, 12)}</p>
            {anomaly?.amount && (
              <>
                <span className="text-gray-300">·</span>
                <p className="text-xs font-semibold text-gray-700">{formatAmount(anomaly?.amount, anomaly?.currency)}</p>
              </>
            )}
            <span className="text-gray-300">·</span>
            <p className="text-xs text-gray-400">{formatDate(anomaly?.detected_at || anomaly?.created_at)}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!anomaly?.dismissed && (
            <>
              <button
                onClick={(e) => { e?.stopPropagation(); onEscalate(anomaly); }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
              >
                <Icon name="AlertOctagon" size={11} />
                Escalader
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); onDismiss(anomaly); }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon name="Check" size={11} />
                Ignorer
              </button>
            </>
          )}
          {anomaly?.dismissed && (
            <span className="text-xs text-gray-400 italic">Ignoré</span>
          )}
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={15} className="text-gray-400" />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4 bg-gray-50 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {[
              { label: 'Type d\'anomalie', value: cfg?.label },
              { label: 'Référence', value: anomaly?.reference || '—' },
              { label: 'Montant', value: formatAmount(anomaly?.amount, anomaly?.currency) },
              { label: 'Détecté le', value: formatDate(anomaly?.detected_at || anomaly?.created_at) },
              { label: 'Source', value: anomaly?.source || 'Paystack Stream' },
              { label: 'Statut', value: anomaly?.dismissed ? 'Ignoré' : 'Actif' },
            ]?.map(item => (
              <div key={item?.label}>
                <p className="text-gray-400 font-medium">{item?.label}</p>
                <p className="text-gray-700 font-mono text-[11px] mt-0.5 break-all">{item?.value}</p>
              </div>
            ))}
          </div>

          {anomaly?.description && (
            <div className="p-3 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-600 mb-1">Description</p>
              <p className="text-xs text-gray-700">{anomaly?.description}</p>
            </div>
          )}

          {anomaly?.related_references?.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs font-semibold text-red-700 mb-1">Références liées (doublons potentiels)</p>
              <div className="flex flex-wrap gap-1.5">
                {anomaly?.related_references?.map((ref, i) => (
                  <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 rounded font-mono text-[10px]">{ref}</span>
                ))}
              </div>
            </div>
          )}

          {anomaly?.raw_data && (
            <details>
              <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">Données brutes</summary>
              <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-[10px] overflow-x-auto max-h-32">
                {JSON.stringify(anomaly?.raw_data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Stats Card ───────────────────────────────────────────────────────────────
const AnomalyStatCard = ({ type, count, onClick, active }) => {
  const cfg = ANOMALY_TYPES?.[type];
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all w-full ${
        active ? `${cfg?.color?.split(' ')?.[0]} ${cfg?.color?.split(' ')?.[2]}` : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg?.color?.split(' ')?.[0]}`}>
        <Icon name={cfg?.icon} size={16} className={cfg?.color?.split(' ')?.[1]} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium truncate">{cfg?.label}</p>
        <p className="text-xl font-bold text-gray-900">{count}</p>
      </div>
      {count > 0 && (
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ml-auto ${cfg?.dotColor} ${count > 0 ? 'animate-pulse' : ''}`} />
      )}
    </button>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PaystackAnomalyDetectionPanel = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showDismissed, setShowDismissed] = useState(false);
  const [newAnomalyIds, setNewAnomalyIds] = useState(new Set());
  const [stats, setStats] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const channelRef = useRef(null);
  const prevCountRef = useRef(0);

  const loadAnomalies = useCallback(async () => {
    try {
      let query = supabase
        ?.from('paystack_anomaly_detections')
        ?.select('*')
        ?.order('detected_at', { ascending: false })
        ?.limit(100);

      if (!showDismissed) {
        query = query?.eq('dismissed', false);
      }

      if (filter !== 'all') {
        query = query?.eq('anomaly_type', filter);
      }

      const { data } = await query;
      const items = data || [];

      // Track new anomalies for highlight
      if (prevCountRef?.current > 0 && items?.length > prevCountRef?.current) {
        const newIds = new Set(items.slice(0, items.length - prevCountRef.current).map(a => a.id));
        setNewAnomalyIds(newIds);
        setTimeout(() => setNewAnomalyIds(new Set()), 8000);
      }
      prevCountRef.current = items?.length;

      setAnomalies(items);

      // Compute stats
      const statsMap = {};
      items?.forEach(a => {
        statsMap[a.anomaly_type] = (statsMap?.[a?.anomaly_type] || 0) + 1;
      });
      setStats(statsMap);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading anomalies:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, showDismissed]);

  useEffect(() => {
    loadAnomalies();

    // Real-time subscription
    channelRef.current = supabase
      ?.channel('paystack-anomaly-realtime')
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'paystack_anomaly_detections' }, () => {
        loadAnomalies();
      })
      ?.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'paystack_anomaly_detections' }, () => {
        loadAnomalies();
      })
      ?.subscribe();

    return () => { supabase?.removeChannel(channelRef?.current); };
  }, [loadAnomalies]);

  const handleDismiss = async (anomaly) => {
    try {
      await supabase
        ?.from('paystack_anomaly_detections')
        ?.update({ dismissed: true, dismissed_at: new Date()?.toISOString() })
        ?.eq('id', anomaly?.id);
      await loadAnomalies();
    } catch (err) {
      console.error('Error dismissing anomaly:', err);
    }
  };

  const handleEscalate = async (anomaly) => {
    try {
      await supabase?.from('notifications')?.insert({
        title: `Anomalie Paystack — ${ANOMALY_TYPES?.[anomaly?.anomaly_type]?.label || anomaly?.anomaly_type}`,
        message: `Anomalie détectée sur la transaction ${anomaly?.reference || anomaly?.id?.slice(0, 8)}. Montant: ${formatAmount(anomaly?.amount, anomaly?.currency)}. Intervention requise.`,
        type: 'admin_alert',
        priority: 'high',
      });
      await supabase
        ?.from('paystack_anomaly_detections')
        ?.update({ escalated: true, escalated_at: new Date()?.toISOString() })
        ?.eq('id', anomaly?.id);
      await loadAnomalies();
    } catch (err) {
      console.error('Error escalating anomaly:', err);
    }
  };

  const handleDismissAll = async () => {
    if (!window.confirm('Ignorer toutes les anomalies visibles ?')) return;
    try {
      const ids = anomalies?.filter(a => !a?.dismissed)?.map(a => a?.id);
      if (ids?.length === 0) return;
      await supabase
        ?.from('paystack_anomaly_detections')
        ?.update({ dismissed: true, dismissed_at: new Date()?.toISOString() })
        ?.in('id', ids);
      await loadAnomalies();
    } catch (err) {
      console.error('Error dismissing all:', err);
    }
  };

  const activeAnomalies = anomalies?.filter(a => !a?.dismissed);
  const criticalCount = anomalies?.filter(a => !a?.dismissed && a?.anomaly_type === 'duplicate_charge')?.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-gray-900">Détection d'Anomalies Paystack</h3>
              {criticalCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">
                  <Icon name="AlertOctagon" size={11} />
                  {criticalCount} critique{criticalCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Surveillance en temps réel du flux de transactions Paystack
              {lastRefresh && <span className="ml-2 text-xs text-gray-400">· Mis à jour {formatDate(lastRefresh)}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-medium text-green-700">Temps réel</span>
            </div>
            <button
              onClick={loadAnomalies}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Icon name="RefreshCw" size={12} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.keys(ANOMALY_TYPES)?.map(type => (
            <AnomalyStatCard
              key={type}
              type={type}
              count={stats?.[type] || 0}
              active={filter === type}
              onClick={() => setFilter(filter === type ? 'all' : type)}
            />
          ))}
        </div>
      </div>

      {/* Filters & controls */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Toutes ({activeAnomalies?.length})
            </button>
            {Object.entries(ANOMALY_TYPES)?.map(([type, cfg]) => (
              <button
                key={type}
                onClick={() => setFilter(filter === type ? 'all' : type)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${filter === type ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {cfg?.label} ({stats?.[type] || 0})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e?.target?.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300"
              />
              Afficher ignorés
            </label>
            {activeAnomalies?.length > 0 && (
              <button
                onClick={handleDismissAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon name="CheckCheck" size={12} />
                Tout ignorer
              </button>
            )}
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            </div>
          ) : anomalies?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3 bg-green-50 rounded-xl border border-green-200">
              <Icon name="ShieldCheck" size={36} className="text-green-500" />
              <p className="text-sm font-bold text-green-700">Aucune anomalie détectée</p>
              <p className="text-xs text-green-600">Le flux de transactions Paystack est normal</p>
            </div>
          ) : (
            <div className="space-y-2">
              {anomalies?.map(anomaly => (
                <AnomalyRow
                  key={anomaly?.id}
                  anomaly={anomaly}
                  onDismiss={handleDismiss}
                  onEscalate={handleEscalate}
                  isNew={newAnomalyIds?.has(anomaly?.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaystackAnomalyDetectionPanel;
